/** @format */

import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Logger } from './logger.service';
import { WINDOW } from './window/window.service';
import { PERSONALIZATION_SERVICE } from '@shared/data-access/enums';
import type { FeatureConfiguration } from '@dsg-tech/pz-web-core';

export interface IPersonalizationService {
  getPersonalizationData(key: string, params?: unknown, timeout?: number): Promise<unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class FeatureConfigurationService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly window = inject(WINDOW);
  private readonly logger = inject(Logger);

  private readonly DEFAULT_PERSONALIZATION_TIMEOUT = 2000;
  private readonly DCSG_PZ_READY_TIMEOUT = 5000;
  private personalizationServiceInstance: Promise<IPersonalizationService> | null = null;

  /**
   * @description
   * Event listener for provided Events enum value. Executes the callback provided
   * when the Event is emitted
   */
  onFeature(key: string, callback: (params: FeatureConfiguration['params']) => void): void {
    if (isPlatformBrowser(this.platformId)) {
      if (typeof this.window['dcsgPz']?.features?.getConfig === 'function') {
        const featureConf = this.window['dcsgPz'].features.getConfig(key, (featureConf) => {
          if (featureConf?.enabled) {
            callback(featureConf.params);
          }
        });
        if (featureConf?.enabled && featureConf?.initialized) {
          callback(featureConf.params);
        }
      }
    }
  }

  /**
   * @description
   * Waits for the dcsgPzReady CustomEvent before resolving, ensuring pz-web-core
   * has finished initializing before any loadService call is attempted.
   * Resolves immediately if loadService is already available (CV-06).
   * Resolves immediately in SSR context (SEC-03).
   * Uses { once: true } to prevent memory leaks (CV-05).
   * Re-verifies loadService availability after event fires to guard against
   * spoofed events (SEC-02).
   * Rejects after DCSG_PZ_READY_TIMEOUT if pz-web-core never dispatches the event,
   * so callers can fall back to originalData instead of waiting forever.
   */
  private waitForDcsgPzReady(): Promise<void> {
    if (typeof this.window['dcsgPz']?.services?.loadService === 'function') {
      return Promise.resolve();
    }
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const onReady = () => {
        if (typeof this.window['dcsgPz']?.services?.loadService === 'function') {
          clearTimeout(timeoutId);
          resolve();
        }
      };
      const timeoutId = setTimeout(() => {
        this.window.removeEventListener('dcsgPzReady', onReady);
        reject(new Error('Timed out waiting for dcsgPzReady event'));
      }, this.DCSG_PZ_READY_TIMEOUT);

      this.window.addEventListener('dcsgPzReady', onReady, { once: true });
    });
  }

  async getPersonalization<T>(
    personalizationKey: string,
    originalData: T,
    options: { timeout?: number } = {},
  ): Promise<T> {
    try {
      await this.waitForDcsgPzReady();

      this.personalizationServiceInstance ??= this.window['dcsgPz']?.services.loadService(
        PERSONALIZATION_SERVICE,
      ) as Promise<IPersonalizationService>;

      const service = await this.personalizationServiceInstance;
      const timeout = options.timeout ?? this.DEFAULT_PERSONALIZATION_TIMEOUT;
      const personalizedData = (await service.getPersonalizationData(
        personalizationKey,
        originalData,
        timeout,
      )) as T | null;
      // service returns `null` if alloy service / AEM call times out
      // so fall back to the original data if that happens
      return personalizedData ?? originalData;
    } catch (error) {
      this.logger.warn('Error getting personalized data:', String(error));
      return originalData;
    }
  }
}
